import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client, unwrap } from "@/api/client";
import type { components } from "@/api/schema";
import { t } from "@/i18n";
import type { Call, CallMode } from "./state";

export type VoiceToken = components["schemas"]["Token"];
export type CallOutcome = NonNullable<components["schemas"]["Call"]["outcome"]>;

/**
 * Mint a Voice access token.
 *
 * Not a `useQuery`: the SDK asks for a fresh one on `tokenWillExpire`, on its
 * own schedule, from outside React. Caching it would hand back the expired one
 * that triggered the refresh.
 */
export async function fetchVoiceToken(): Promise<VoiceToken> {
  return unwrap(client.POST("/api/telephony/token/"), t("errors.voiceToken"));
}

export type StartCallInput = {
  mode: CallMode;
  /** A `ContactPhone` pk. Preferred: it is what links the call to a company. */
  phone?: number;
  /** A hand-typed prospecting number, when there is no `ContactPhone` yet. */
  number?: string;
  /** From the *Llamar* keypad. The server checks the number and whose line it leaves from. */
  keypad?: boolean;
};

export function useStartCall() {
  return useMutation({
    mutationFn: (input: StartCallInput): Promise<Call> =>
      unwrap(
        client.POST("/api/telephony/calls/", {
          body: {
            mode: input.mode,
            ...(input.phone !== undefined ? { phone: input.phone } : {}),
            ...(input.number !== undefined ? { number: input.number } : {}),
            keypad: input.keypad ?? false,
          },
        }),
        t("errors.startCall"),
      ),
  });
}

export function useHangup() {
  return useMutation({
    mutationFn: (ref: string) =>
      unwrap(
        client.POST("/api/telephony/calls/{ref}/hangup/", {
          params: { path: { ref } },
        }),
        t("errors.hangup"),
      ),
  });
}

export function useCall(ref: string | null) {
  return useQuery({
    queryKey: ["call", ref],
    enabled: ref !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/telephony/calls/{ref}/", {
          params: { path: { ref: ref as string } },
        }),
        t("errors.call"),
      ),
  });
}

function byActivity(page: { results: Call[] }): Map<number, Call> {
  return new Map(
    page.results.flatMap((call) =>
      call.activity === null ? [] : [[call.activity, call] as const],
    ),
  );
}

/**
 * The calls behind a page of timeline entries, in one request, keyed by entry.
 *
 * Looked up by `activity` because the entry is all a timeline holds, and the
 * call's `ref` — what the recording endpoint is addressed by — only the call
 * knows. The server scopes it like any call read: a seller finds their own
 * calls, a manager everyone's. An entry whose call does not come back is one
 * this reader may not open, and the timeline falls back to its comment.
 */
export function useCallsForActivities(activityIds: number[]) {
  return useQuery({
    queryKey: ["calls", "byActivity", activityIds],
    enabled: activityIds.length > 0,
    queryFn: () =>
      unwrap(
        client.GET("/api/telephony/calls/", {
          params: { query: { activity__in: activityIds } },
        }),
        t("errors.call"),
      ),
    select: byActivity,
  });
}

/**
 * The write-up: outcome, notes, and the two consent answers.
 *
 * Saving it is what puts the call on the client's timeline (D9), and — when
 * the agent asked — what records demonstrable opt-in with the recording behind
 * it (.docs/06 §2.1, mechanism 3). One request, deliberately: a second call
 * for the consent could fail after the notes had already saved, leaving the
 * agent certain they recorded something that was never written.
 *
 * `null` on a consent answer means the agent did not ask, and the server
 * leaves the contact alone. `false` means they asked and were told no.
 */
export function useSaveCallNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ref,
      outcome,
      notes,
      consentWhatsapp = null,
      consentRecording = null,
    }: {
      ref: string;
      outcome: CallOutcome | null;
      notes: string;
      consentWhatsapp?: boolean | null;
      consentRecording?: boolean | null;
    }) =>
      unwrap(
        client.PATCH("/api/telephony/calls/{ref}/", {
          params: { path: { ref } },
          body: {
            outcome,
            notes,
            consent_whatsapp: consentWhatsapp,
            consent_recording: consentRecording,
          },
        }),
        t("errors.saveNote"),
      ),
    onSuccess: (call, { consentWhatsapp = null, consentRecording = null }) => {
      void queryClient.invalidateQueries({ queryKey: ["call", call.ref] });
      // The write-up wrote an `Activity`, so every open timeline is stale.
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      // And it may have moved a consent flag, which the contact screen shows.
      void queryClient.invalidateQueries({ queryKey: ["contact"] });
      // So do the company's contact lists, and an answer to *Grabación* lights
      // a tag on the company — its file, the list and the event modal the call
      // was made from.
      if (consentWhatsapp !== null || consentRecording !== null) {
        void queryClient.invalidateQueries({ queryKey: ["contacts"] });
        void queryClient.invalidateQueries({ queryKey: ["company"] });
        void queryClient.invalidateQueries({ queryKey: ["companies"] });
      }
    },
  });
}

/**
 * File a cold call under the company, or the contact, it turned into.
 *
 * The call was logged with neither, because neither existed. Linking moves the
 * call and the entry it wrote onto the new account, so its timeline opens with
 * the call that won it instead of empty.
 */
export function useLinkCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ref,
      company,
      contact,
    }: {
      ref: string;
      company?: number;
      contact?: number;
    }) =>
      unwrap(
        client.POST("/api/telephony/calls/{ref}/link/", {
          params: { path: { ref } },
          body: {
            ...(company !== undefined ? { company } : {}),
            ...(contact !== undefined ? { contact } : {}),
          },
        }),
        t("errors.linkCall"),
      ),
    onSuccess: (call) => {
      // Written straight in: the wrap-up reads this to decide what to offer
      // next, and a refetch would show the previous step for a moment.
      queryClient.setQueryData(["call", call.ref], call);
      // The entry changed accounts, so every list that shows it is stale.
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}

/**
 * Where the audio lives. A plain URL, not a fetch: the endpoint 302s to a
 * short-lived presigned object and `<audio>` follows that itself. Reading it
 * through JS would mean holding client PII in a blob for as long as the tab
 * stays open.
 *
 * `reason` is written to `RecordingAccessLog` — the compliance half
 * (.docs/06 §5). Every play is attributable.
 */
export function recordingUrl(ref: string, reason: string): string {
  return `/api/telephony/calls/${encodeURIComponent(
    ref,
  )}/recording/?reason=${encodeURIComponent(reason)}`;
}
